import { useQueries } from "@tanstack/react-query";
import { Link, useNavigate, useParams } from "@tanstack/react-router";
import type { ColumnDef } from "@tanstack/react-table";
import { Archive, Droplets, MapPin, Pencil, Plus, PowerOff, RotateCcw } from "lucide-react";
import { useMemo, useState } from "react";
import { api, unwrap } from "@/api/client";
import {
  useCreateGenerator,
  useCreateSite,
  useCreateTank,
  useOrganization,
  useRefills,
  useRegions,
  useSite,
  useSiteGenerators,
  useSites,
  useSiteTanks,
  useUpdateGenerator,
  useUpdateSite,
  useUpdateTank,
} from "@/api/hooks";
import { qk } from "@/api/keys";
import { ApiError } from "@/api/problem";
import type { FuelType, Generator, Refill, Site, Tank } from "@/api/types";
import { useActiveOrgId, useSession } from "@/auth/session";
import { PageHeader } from "@/components/app-shell";
import { DataTable } from "@/components/ui/data-table";
import { Dialog } from "@/components/ui/dialog";
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  CardTitle,
  Field,
  Input,
  MicroLabel,
  Num,
  NumberInput,
  Skeleton,
} from "@/components/ui/primitives";
import { NONE, Select } from "@/components/ui/select";
import { LevelBar, SightGlass } from "@/components/ui/sight-glass";
import { EmptyState, ErrorState, FormError, LoadingBlock } from "@/components/ui/states";
import { assessTanks, sortByUrgency, type RiskLevel, type TankRisk } from "@/domain/risk";
import { formatDate, formatHours, formatLiters, formatNumber } from "@/lib/format";
import type { TKey } from "@/i18n/dict";
import { useI18n } from "@/i18n/provider";
import { RefillsTable } from "./refills";

/**
 * Sites.
 *
 * A site is not a row of metadata. It is a place that either has fuel or is about to not,
 * so the list leads with the LEVEL of its worst tank and the name comes second — the same
 * order in which an operations manager reads a yard: you see the short column first and
 * learn whose it is afterwards.
 *
 * NOTHING ON THIS SCREEN DESTROYS ANYTHING. A site is ARCHIVED, a tank or generator is
 * DECOMMISSIONED: they leave the lists, they refuse new writes, and they keep every liter
 * of their history. That is what the server does, so it is what the buttons are called.
 * The one thing this screen must never do is offer a "Delete" that comes back 409.
 */

const RISK_TONE: Record<RiskLevel, "dry" | "low" | "idle" | "ok"> = {
  dry: "dry",
  low: "low",
  overdue: "idle",
  ok: "ok",
};

const RISK_LABEL: Record<RiskLevel, TKey> = {
  dry: "dash.dry",
  low: "dash.low",
  overdue: "dash.overdue",
  ok: "sites.status.active",
};

const FUEL_TYPES: FuelType[] = ["diesel", "petrol", "kerosene"];

/**
 * Every tank in the org, one request per site.
 *
 * The spec has no `GET /v1/tanks` — tanks are only listed under their site — so the list
 * screen fans out exactly as the dashboard does. It is an N+1 and it is flagged to the
 * backend team; it survives here because the requests are parallel, cached per site under
 * the same key the detail page uses, and an org has tens of sites, not thousands.
 */
function useTanksOfSites(siteIds: string[]) {
  const orgId = useActiveOrgId();

  return useQueries({
    queries: siteIds.map((siteId) => ({
      queryKey: qk.siteTanks(orgId, siteId),
      queryFn: () =>
        unwrap(api.GET("/v1/sites/{siteId}/tanks", { params: { path: { siteId } } })),
    })),
    combine: (results) => ({
      tanks: results.flatMap((r) => r.data?.data ?? []) as Tank[],
      isPending: results.some((r) => r.isPending),
      error: results.find((r) => r.error)?.error,
    }),
  });
}

/** The site, plus the one tank at it that decides whether today is a good day. */
interface SiteRow {
  site: Site;
  worst: TankRisk | null;
  /** 0–100, or 999 when unknown — so "no reading" sorts last instead of looking empty. */
  sortKey: number;
}

/* ------------------------------------------------------------- site list */

export function SitesPage() {
  const { t, lang } = useI18n();
  const navigate = useNavigate();
  const { can } = useSession();

  const [search, setSearch] = useState("");
  const [regionId, setRegionId] = useState<string>(NONE);
  const [showArchived, setShowArchived] = useState(false);
  const [creating, setCreating] = useState(false);

  const regionsQuery = useRegions();

  const sitesQuery = useSites({
    ...(search ? { q: search } : {}),
    ...(regionId !== NONE ? { region_id: regionId } : {}),
  });

  // An archived site is out of service, so it is out of the list — unless asked for.
  //
  // This is filtered here rather than by adding `status: "active"` to the query above on
  // purpose: the status is a VIEW of the same rows, and putting it in the query would fork
  // the cache into two entries per org that must then be invalidated in lockstep. One key
  // per (org, search, region) is the thing the tenant-isolation test pins, and it is right.
  const sites = useMemo(
    () =>
      (sitesQuery.data?.data ?? []).filter(
        (site) => showArchived || site.status === "active",
      ),
    [sitesQuery.data, showArchived],
  );
  const tanksQuery = useTanksOfSites(sites.map((s) => s.id));
  const orgQuery = useOrganization();
  const refillsQuery = useRefills({ limit: 100 });

  const now = useMemo(() => new Date(), []);

  // The SAME risk assessment the dashboard runs, from the same module. If this screen
  // computed "low" its own way, the two would eventually disagree in front of a customer.
  const rows = useMemo<SiteRow[]>(() => {
    const worstBySite = new Map<string, TankRisk>();

    if (orgQuery.data) {
      const tanksBySite = new Map<string, Tank[]>();
      for (const tank of tanksQuery.tanks) {
        const list = tanksBySite.get(tank.site_id) ?? [];
        list.push(tank);
        tanksBySite.set(tank.site_id, list);
      }

      const refillsByTank = new Map<string, Refill[]>();
      for (const refill of refillsQuery.data?.data ?? []) {
        const list = refillsByTank.get(refill.tank_id) ?? [];
        list.push(refill);
        refillsByTank.set(refill.tank_id, list);
      }

      // Worst first — so the first risk seen for a site IS that site's worst tank.
      for (const risk of sortByUrgency(
        assessTanks({
          sites,
          tanksBySite,
          refillsByTank,
          orgLowThresholdPercent: orgQuery.data.settings.low_fuel_threshold_percent,
          orgRefillOverdueDays: orgQuery.data.settings.refill_overdue_days,
          now,
        }),
      )) {
        if (!worstBySite.has(risk.site.id)) worstBySite.set(risk.site.id, risk);
      }
    }

    return (
      sites
        .map((site) => {
          const worst = worstBySite.get(site.id) ?? null;
          return { site, worst, sortKey: worst?.percent ?? 999 };
        })
        // Worst first, before the user has clicked anything. A list of sites sorted by name
        // is a filing cabinet; a list sorted by how close each one is to darkness is the
        // product. The column headers are still there for anyone who wants it by name.
        .sort((a, b) => a.sortKey - b.sortKey)
    );
  }, [sites, tanksQuery.tanks, refillsQuery.data, orgQuery.data, now]);

  const levelsPending = tanksQuery.isPending || orgQuery.isPending;

  const columns = useMemo<ColumnDef<SiteRow, unknown>[]>(
    () => [
      {
        id: "level",
        accessorFn: (row) => row.sortKey,
        // Sorted ascending by default — and ascending means EMPTIEST FIRST, which is the
        // only ordering this column has any business having.
        header: () => t("sites.worstTank"),
        cell: ({ row }) => {
          if (levelsPending) return <Skeleton className="h-10 w-24" />;

          const { worst } = row.original;
          if (!worst) {
            return <span className="text-xs text-faint">{t("sites.noTanksYet")}</span>;
          }

          return (
            <div className="flex items-center gap-3">
              <SightGlass
                percent={worst.percent}
                level={worst.level}
                className="h-12 w-5"
                label={`${row.original.site.name} — ${
                  worst.percent === null ? t("tank.levelUnknown") : `${Math.round(worst.percent)}%`
                }`}
              />
              <div className="flex min-w-24 flex-col gap-1">
                <span className="font-mono text-sm text-text">
                  {worst.percent === null ? (
                    <span className="text-xs text-faint">{t("tank.levelUnknown")}</span>
                  ) : (
                    <Num>{formatNumber(lang, Math.round(worst.percent))}%</Num>
                  )}
                </span>
                <LevelBar percent={worst.percent} level={worst.level} />
                {worst.tank.current_level_liters !== undefined ? (
                  <span className="font-mono text-[11px] text-faint">
                    <Num>
                      {formatLiters(lang, worst.tank.current_level_liters)}
                      {" / "}
                      {formatLiters(lang, worst.tank.capacity_liters)} {t("common.liters")}
                    </Num>
                  </span>
                ) : null}
              </div>
            </div>
          );
        },
      },
      {
        id: "name",
        accessorFn: (row) => row.site.name,
        header: () => t("sites.name"),
        cell: ({ row }) => (
          <div className="flex flex-col">
            {/* A real link, so the row is navigable by keyboard and openable in a new tab. */}
            <Link
              to="/sites/$siteId"
              params={{ siteId: row.original.site.id }}
              className="font-medium text-text underline-offset-4 hover:underline"
              onClick={(event) => event.stopPropagation()}
            >
              {row.original.site.name}
            </Link>
            {row.original.site.address ? (
              <span className="text-xs text-faint">{row.original.site.address}</span>
            ) : null}
          </div>
        ),
      },
      {
        id: "code",
        accessorFn: (row) => row.site.code ?? "",
        header: () => t("sites.code"),
        cell: ({ row }) =>
          row.original.site.code ? (
            <span className="font-mono text-xs text-muted" dir="ltr">
              {row.original.site.code}
            </span>
          ) : (
            <span className="text-faint">—</span>
          ),
      },
      {
        id: "region",
        accessorFn: (row) => row.site.region?.name ?? "",
        header: () => t("sites.region"),
        cell: ({ row }) =>
          row.original.site.region?.name ?? <span className="text-faint">—</span>,
      },
      {
        id: "tanks",
        accessorFn: (row) => row.site.tank_count,
        header: () => t("sites.tanks"),
        cell: ({ row }) => <Num>{formatNumber(lang, row.original.site.tank_count)}</Num>,
      },
      {
        id: "generators",
        accessorFn: (row) => row.site.generator_count,
        header: () => t("sites.generators"),
        cell: ({ row }) => <Num>{formatNumber(lang, row.original.site.generator_count)}</Num>,
      },
      {
        id: "last_refill_at",
        accessorFn: (row) => row.site.last_refill_at ?? "",
        header: () => t("sites.lastRefill"),
        cell: ({ row }) =>
          row.original.site.last_refill_at ? (
            <Num className="text-xs">{formatDate(lang, row.original.site.last_refill_at)}</Num>
          ) : (
            <span className="text-xs text-faint">{t("dash.neverRefilled")}</span>
          ),
      },
      {
        id: "status",
        accessorFn: (row) => row.site.status,
        header: () => t("sites.status"),
        cell: ({ row }) => {
          const { site, worst } = row.original;

          // An archived site says so, and says nothing else — its tanks raise no alerts.
          if (site.status !== "active") {
            return <Badge tone="idle">{t("sites.archived")}</Badge>;
          }
          if (!worst) return <Badge tone="idle">{t("sites.status.active")}</Badge>;

          return <Badge tone={RISK_TONE[worst.level]}>{t(RISK_LABEL[worst.level])}</Badge>;
        },
      },
    ],
    [t, lang, levelsPending],
  );

  const regionOptions = useMemo(
    () => [
      { value: NONE, label: t("sites.allRegions") },
      ...(regionsQuery.data?.data ?? []).map((region) => ({
        value: region.id,
        label: region.name,
      })),
    ],
    [regionsQuery.data, t],
  );

  const filtered = search !== "" || regionId !== NONE;

  return (
    <>
      <PageHeader
        title={t("sites.title")}
        actions={
          // A viewer or an operator never sees this: the server would refuse them anyway.
          can("sites:write") ? (
            <Button onClick={() => setCreating(true)}>
              <Plus className="size-4" aria-hidden />
              {t("sites.new")}
            </Button>
          ) : null
        }
      />

      <Card>
        <CardHeader className="flex-wrap gap-3">
          <div className="flex flex-1 flex-wrap items-center gap-3">
            <Input
              type="search"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder={t("sites.searchPlaceholder")}
              aria-label={t("common.search")}
              className="max-w-xs"
            />

            <div className="w-44">
              <Select
                value={regionId}
                onValueChange={setRegionId}
                options={regionOptions}
                aria-label={t("sites.filterRegion")}
              />
            </div>
          </div>

          <label className="flex shrink-0 items-center gap-2 text-xs text-muted">
            <input
              type="checkbox"
              checked={showArchived}
              onChange={(event) => setShowArchived(event.target.checked)}
              className="size-4 rounded-[4px] border-rule-strong bg-paper-sunk accent-cream"
            />
            {t("sites.showArchived")}
          </label>
        </CardHeader>

        <DataTable
          caption={t("sites.title")}
          columns={columns}
          data={sitesQuery.data ? rows : undefined}
          isLoading={sitesQuery.isPending}
          error={sitesQuery.error}
          onRetry={() => void sitesQuery.refetch()}
          onRowClick={(row) =>
            void navigate({ to: "/sites/$siteId", params: { siteId: row.site.id } })
          }
          empty={
            <EmptyState
              icon={<MapPin className="size-6" />}
              title={filtered ? t("empty.title") : t("sites.empty")}
              body={filtered ? undefined : t("sites.emptyBody")}
              action={
                can("sites:write") && !filtered ? (
                  <Button size="sm" onClick={() => setCreating(true)}>
                    {t("sites.new")}
                  </Button>
                ) : null
              }
            />
          }
        />
      </Card>

      <SiteFormDialog mode="create" open={creating} onOpenChange={setCreating} />
    </>
  );
}

/* ----------------------------------------------------------- site detail */

export function SiteDetailPage() {
  const { t, lang } = useI18n();
  // Not `from: "/_app/sites/$siteId"`: pinning the route id ties this screen to one place in
  // one route tree, and it is the same component wherever it is mounted. The id comes from
  // the path either way.
  const { siteId } = useParams({ strict: false }) as { siteId: string };
  const { can } = useSession();

  const siteQuery = useSite(siteId);
  const tanksQuery = useSiteTanks(siteId);
  const generatorsQuery = useSiteGenerators(siteId);
  const refillsQuery = useRefills({ site_id: siteId, limit: 25 });
  const orgQuery = useOrganization();

  const [editing, setEditing] = useState(false);
  const [archiving, setArchiving] = useState(false);
  const [addingTank, setAddingTank] = useState(false);
  const [addingGenerator, setAddingGenerator] = useState(false);
  const [editingTank, setEditingTank] = useState<Tank | null>(null);
  const [retiringTank, setRetiringTank] = useState<Tank | null>(null);
  const [editingGenerator, setEditingGenerator] = useState<Generator | null>(null);
  const [retiringGenerator, setRetiringGenerator] = useState<Generator | null>(null);
  const [showRetiredTanks, setShowRetiredTanks] = useState(false);
  const [showRetiredGenerators, setShowRetiredGenerators] = useState(false);

  const updateSite = useUpdateSite(siteId);

  const orgThreshold = orgQuery.data?.settings.low_fuel_threshold_percent ?? 20;

  const allTanks = tanksQuery.data?.data ?? [];
  const liveTanks = allTanks.filter((tank) => tank.status === "active");
  const retiredTanks = allTanks.filter((tank) => tank.status !== "active");

  const allGenerators = generatorsQuery.data?.data ?? [];
  const liveGenerators = allGenerators.filter((g) => g.status !== "decommissioned");
  const retiredGenerators = allGenerators.filter((g) => g.status === "decommissioned");

  if (siteQuery.isPending) return <LoadingBlock />;
  if (siteQuery.error)
    return <ErrorState error={siteQuery.error} onRetry={() => void siteQuery.refetch()} />;

  const site = siteQuery.data;
  if (!site) return <ErrorState error={null} />;

  const archived = site.status !== "active";

  return (
    <>
      <PageHeader
        title={site.name}
        description={
          [site.code, site.region?.name, site.address].filter(Boolean).join(" · ") || undefined
        }
        actions={
          <>
            {/* An archived site accepts no new refills — the server says so. So the button
                that would 403 is not rendered; the one that un-archives it is. */}
            {can("refills:create") && !archived ? (
              <Button asChild>
                <Link to="/refills/new" search={{ siteId: site.id }}>
                  <Droplets className="size-4" aria-hidden />
                  {t("dash.recordRefill")}
                </Link>
              </Button>
            ) : null}

            {can("sites:write") ? (
              <Button variant="secondary" onClick={() => setEditing(true)}>
                <Pencil className="size-4" aria-hidden />
                {t("sites.edit")}
              </Button>
            ) : null}

            {can("sites:write") ? (
              archived ? (
                <Button
                  variant="secondary"
                  disabled={updateSite.isPending}
                  onClick={() => updateSite.mutate({ status: "active" })}
                >
                  <RotateCcw className="size-4" aria-hidden />
                  {t("sites.restore")}
                </Button>
              ) : (
                // NOT `danger`. Archiving destroys nothing, and `dry` means one thing in
                // this product: fuel running out. A red archive button would be a lie in
                // the only colour the operator is trained to react to.
                <Button variant="secondary" onClick={() => setArchiving(true)}>
                  <Archive className="size-4" aria-hidden />
                  {t("sites.archive")}
                </Button>
              )
            ) : null}
          </>
        }
      />

      {archived ? (
        <div
          role="status"
          className="mb-4 flex items-center gap-2 rounded-[6px] border border-rule-strong bg-paper-sunk px-3 py-2 text-sm text-muted"
        >
          <Archive className="size-4 shrink-0 text-mauve" aria-hidden />
          {t("sites.archivedNotice")}
        </div>
      ) : null}

      <div className="grid gap-4 lg:grid-cols-2">
        {/* ---- tanks ---- */}
        <Card>
          <CardHeader>
            <CardTitle>{t("sites.detail.tanks")}</CardTitle>
            {can("sites:write") ? (
              <Button variant="ghost" size="sm" onClick={() => setAddingTank(true)}>
                <Plus className="size-4" aria-hidden />
                {t("sites.addTank")}
              </Button>
            ) : null}
          </CardHeader>

          {tanksQuery.isPending ? (
            <LoadingBlock />
          ) : tanksQuery.error ? (
            <ErrorState error={tanksQuery.error} onRetry={() => void tanksQuery.refetch()} />
          ) : allTanks.length === 0 ? (
            <EmptyState
              title={t("sites.detail.noTanks")}
              action={
                can("sites:write") ? (
                  <Button size="sm" onClick={() => setAddingTank(true)}>
                    {t("sites.addTank")}
                  </Button>
                ) : null
              }
            />
          ) : (
            <CardBody className="flex flex-col gap-5">
              {liveTanks.map((tank) => (
                <TankRow
                  key={tank.id}
                  tank={tank}
                  orgThreshold={orgThreshold}
                  onEdit={() => setEditingTank(tank)}
                  onRetire={() => setRetiringTank(tank)}
                />
              ))}

              {liveTanks.length === 0 ? (
                <p className="py-4 text-center text-sm text-muted">
                  {t("sites.detail.noTanks")}
                </p>
              ) : null}

              {retiredTanks.length > 0 ? (
                <RetiredSection
                  open={showRetiredTanks}
                  onToggle={() => setShowRetiredTanks((v) => !v)}
                  showLabel={t("tank.outOfService", { n: formatNumber(lang, retiredTanks.length) })}
                  hideLabel={t("tank.hideOutOfService")}
                >
                  {retiredTanks.map((tank) => (
                    <TankRow
                      key={tank.id}
                      tank={tank}
                      orgThreshold={orgThreshold}
                      onEdit={() => setEditingTank(tank)}
                      onRetire={() => setRetiringTank(tank)}
                    />
                  ))}
                </RetiredSection>
              ) : null}
            </CardBody>
          )}
        </Card>

        {/* ---- generators ---- */}
        <Card>
          <CardHeader>
            <CardTitle>{t("sites.detail.generators")}</CardTitle>
            {can("sites:write") ? (
              <Button variant="ghost" size="sm" onClick={() => setAddingGenerator(true)}>
                <Plus className="size-4" aria-hidden />
                {t("sites.addGenerator")}
              </Button>
            ) : null}
          </CardHeader>

          {generatorsQuery.isPending ? (
            <LoadingBlock />
          ) : generatorsQuery.error ? (
            <ErrorState
              error={generatorsQuery.error}
              onRetry={() => void generatorsQuery.refetch()}
            />
          ) : allGenerators.length === 0 ? (
            <EmptyState
              title={t("sites.detail.noGenerators")}
              action={
                can("sites:write") ? (
                  <Button size="sm" onClick={() => setAddingGenerator(true)}>
                    {t("sites.addGenerator")}
                  </Button>
                ) : null
              }
            />
          ) : (
            <CardBody className="flex flex-col gap-3">
              {liveGenerators.map((generator) => (
                <GeneratorRow
                  key={generator.id}
                  generator={generator}
                  onEdit={() => setEditingGenerator(generator)}
                  onRetire={() => setRetiringGenerator(generator)}
                />
              ))}

              {liveGenerators.length === 0 ? (
                <p className="py-4 text-center text-sm text-muted">
                  {t("sites.detail.noGenerators")}
                </p>
              ) : null}

              {retiredGenerators.length > 0 ? (
                <RetiredSection
                  open={showRetiredGenerators}
                  onToggle={() => setShowRetiredGenerators((v) => !v)}
                  showLabel={t("gen.outOfService", {
                    n: formatNumber(lang, retiredGenerators.length),
                  })}
                  hideLabel={t("gen.hideOutOfService")}
                >
                  {retiredGenerators.map((generator) => (
                    <GeneratorRow
                      key={generator.id}
                      generator={generator}
                      onEdit={() => setEditingGenerator(generator)}
                      onRetire={() => setRetiringGenerator(generator)}
                    />
                  ))}
                </RetiredSection>
              ) : null}
            </CardBody>
          )}
        </Card>
      </div>

      {/* ---- the ledger for this site. It outlives everything above it. ---- */}
      <Card className="mt-4">
        <CardHeader>
          <CardTitle>{t("sites.detail.history")}</CardTitle>
        </CardHeader>

        <RefillsTable
          query={refillsQuery}
          empty={<EmptyState title={t("sites.detail.noRefills")} />}
          hideSiteColumn
        />
      </Card>

      {/* An edit dialog is MOUNTED WHEN ITS ROW IS PICKED, and keyed by that row's id.
          Rendering it permanently and merely toggling `open` would freeze its `useState`
          initial values at the first render — when there is no row — and the form would
          open with every field blank, quietly offering to erase the tank's capacity. It
          did. A green test suite did not notice; opening the dialog and looking did. */}
      {editing ? (
        <SiteFormDialog
          key={site.id}
          mode="edit"
          site={site}
          open
          onOpenChange={setEditing}
        />
      ) : null}

      <ArchiveSiteDialog site={site} open={archiving} onOpenChange={setArchiving} />

      <TankFormDialog
        siteId={siteId}
        mode="create"
        open={addingTank}
        onOpenChange={setAddingTank}
      />
      {editingTank ? (
        <TankFormDialog
          key={editingTank.id}
          siteId={siteId}
          mode="edit"
          tank={editingTank}
          open
          onOpenChange={(open) => !open && setEditingTank(null)}
        />
      ) : null}
      <DecommissionTankDialog
        siteId={siteId}
        tank={retiringTank}
        open={retiringTank !== null}
        onOpenChange={(open) => !open && setRetiringTank(null)}
      />

      <GeneratorFormDialog
        siteId={siteId}
        mode="create"
        open={addingGenerator}
        onOpenChange={setAddingGenerator}
      />
      {editingGenerator ? (
        <GeneratorFormDialog
          key={editingGenerator.id}
          siteId={siteId}
          mode="edit"
          generator={editingGenerator}
          open
          onOpenChange={(open) => !open && setEditingGenerator(null)}
        />
      ) : null}
      <DecommissionGeneratorDialog
        siteId={siteId}
        generator={retiringGenerator}
        open={retiringGenerator !== null}
        onOpenChange={(open) => !open && setRetiringGenerator(null)}
      />
    </>
  );
}

/** The out-of-service drawer: gone from the list, never gone from the product. */
function RetiredSection({
  open,
  onToggle,
  showLabel,
  hideLabel,
  children,
}: {
  open: boolean;
  onToggle: () => void;
  showLabel: string;
  hideLabel: string;
  children: React.ReactNode;
}) {
  return (
    <div className="border-t border-rule pt-3">
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={open}
        className="label-caps font-mono text-[10px] font-medium text-faint transition-colors hover:text-text focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-cream"
      >
        {open ? hideLabel : showLabel}
      </button>

      {open ? <div className="mt-4 flex flex-col gap-5 opacity-70">{children}</div> : null}
    </div>
  );
}

/* ------------------------------------------------------------- tank row */

function TankRow({
  tank,
  orgThreshold,
  onEdit,
  onRetire,
}: {
  tank: Tank;
  orgThreshold: number;
  onEdit: () => void;
  onRetire: () => void;
}) {
  const { t, lang } = useI18n();
  const { can } = useSession();

  const percent = tank.level_percent ?? null;
  const threshold = tank.low_level_threshold_percent ?? orgThreshold;

  // A decommissioned tank has nothing to say: it holds no fuel anyone is relying on, so it
  // does not get to be red. `dry` is reserved for fuel that is actually running out.
  const level: RiskLevel =
    tank.status !== "active" ? "ok" : percent !== null && percent <= threshold ? "low" : "ok";

  const retired = tank.status !== "active";

  return (
    <div className="flex items-start gap-4">
      <SightGlass
        percent={retired ? null : percent}
        level={level}
        className="h-20"
        label={`${tank.name} — ${
          percent === null ? t("tank.levelUnknown") : `${Math.round(percent)}%`
        }`}
      />

      <div className="flex min-w-0 flex-1 flex-col gap-1.5">
        <div className="flex items-baseline justify-between gap-2">
          <span className="truncate text-sm font-medium text-text">{tank.name}</span>

          <div className="flex shrink-0 items-center gap-2">
            <Badge tone={retired ? "idle" : "ok"}>
              {t(retired ? "tank.status.decommissioned" : "tank.status.active")}
            </Badge>

            {can("sites:write") ? (
              <>
                {/* Named for the row it acts on: a site has several tanks, and "Edit tank"
                    three times over is three identical buttons to a screen reader. */}
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={onEdit}
                  aria-label={`${t("tank.edit")} — ${tank.name}`}
                >
                  <Pencil className="size-3.5" aria-hidden />
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={onRetire}
                  aria-label={
                    retired
                      ? `${t("tank.recommission")} — ${tank.name}`
                      : `${t("tank.decommission")} — ${tank.name}`
                  }
                >
                  {retired ? (
                    <RotateCcw className="size-3.5" aria-hidden />
                  ) : (
                    <PowerOff className="size-3.5" aria-hidden />
                  )}
                </Button>
              </>
            ) : null}
          </div>
        </div>

        <p className="font-mono text-lg text-text">
          {tank.current_level_liters !== undefined ? (
            <Num>
              {formatLiters(lang, tank.current_level_liters)}
              <span className="text-sm text-faint">
                {" / "}
                {formatLiters(lang, tank.capacity_liters)} {t("common.liters")}
              </span>
            </Num>
          ) : (
            <span className="text-sm text-faint">{t("tank.levelUnknown")}</span>
          )}
        </p>

        <LevelBar percent={retired ? null : percent} level={level} />

        <p className="text-xs text-faint">
          {t(`tank.fuel.${tank.fuel_type}` as const)}
          {" · "}
          <Num>
            {t("tank.threshold")} {formatNumber(lang, threshold)}%
          </Num>
          {tank.level_updated_at ? ` · ${formatDate(lang, tank.level_updated_at)}` : ""}
        </p>
      </div>
    </div>
  );
}

/* -------------------------------------------------------- generator row */

function GeneratorRow({
  generator,
  onEdit,
  onRetire,
}: {
  generator: Generator;
  onEdit: () => void;
  onRetire: () => void;
}) {
  const { t, lang } = useI18n();
  const { can } = useSession();

  const retired = generator.status === "decommissioned";

  return (
    <div className="flex flex-wrap items-center justify-between gap-3 border-b border-rule pb-3 last:border-b-0 last:pb-0">
      <div className="min-w-0">
        <p className="truncate text-sm font-medium text-text">{generator.name}</p>
        <p className="truncate text-xs text-faint">
          {[generator.manufacturer, generator.model, generator.serial_number]
            .filter(Boolean)
            .join(" · ") || "—"}
        </p>
      </div>

      <div className="flex shrink-0 items-center gap-4">
        <div className="text-end">
          <MicroLabel>{t("gen.kva")}</MicroLabel>
          <p className="font-mono text-sm text-text">
            <Num>{formatNumber(lang, generator.kva_rating)}</Num>
          </p>
        </div>

        <div className="text-end">
          <MicroLabel>{t("gen.runningHours")}</MicroLabel>
          <p className="font-mono text-sm text-text">
            <Num>{formatHours(lang, generator.running_hours)}</Num>
          </p>
        </div>

        <Badge
          tone={
            generator.status === "active"
              ? "ok"
              : generator.status === "maintenance"
                ? "low"
                : "idle"
          }
        >
          {t(`gen.status.${generator.status}` as const)}
        </Badge>

        {can("sites:write") ? (
          <div className="flex items-center gap-1">
            <Button
              variant="ghost"
              size="sm"
              onClick={onEdit}
              aria-label={`${t("gen.edit")} — ${generator.name}`}
            >
              <Pencil className="size-3.5" aria-hidden />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={onRetire}
              aria-label={
                retired
                  ? `${t("gen.recommission")} — ${generator.name}`
                  : `${t("gen.decommission")} — ${generator.name}`
              }
            >
              {retired ? (
                <RotateCcw className="size-3.5" aria-hidden />
              ) : (
                <PowerOff className="size-3.5" aria-hidden />
              )}
            </Button>
          </div>
        ) : null}
      </div>
    </div>
  );
}

/* -------------------------------------------------------- site dialogs */

function useFieldErrors(error: unknown): Record<string, string> {
  return error instanceof ApiError ? error.fieldErrors : {};
}

function SiteFormDialog({
  mode,
  site,
  open,
  onOpenChange,
}: {
  mode: "create" | "edit";
  site?: Site;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { t } = useI18n();
  const navigate = useNavigate();
  const regionsQuery = useRegions();

  const createSite = useCreateSite();
  const updateSite = useUpdateSite(site?.id ?? "");
  const mutation = mode === "create" ? createSite : updateSite;

  const [name, setName] = useState(site?.name ?? "");
  const [code, setCode] = useState(site?.code ?? "");
  const [address, setAddress] = useState(site?.address ?? "");
  const [regionId, setRegionId] = useState(site?.region?.id ?? NONE);

  // The dialog is mounted for the row it edits, and remounted when that row changes —
  // keying on the site id is what makes these initial values correct rather than stale.
  const fieldErrors = useFieldErrors(mutation.error);

  const regionOptions = useMemo(
    () => [
      { value: NONE, label: t("sites.noRegion") },
      ...(regionsQuery.data?.data ?? []).map((region) => ({
        value: region.id,
        label: region.name,
      })),
    ],
    [regionsQuery.data, t],
  );

  const submit = () => {
    const body = {
      name: name.trim(),
      ...(code.trim() ? { code: code.trim() } : {}),
      ...(address.trim() ? { address: address.trim() } : {}),
      ...(regionId !== NONE ? { region_id: regionId } : {}),
    };

    if (mode === "create") {
      createSite.mutate(body, {
        onSuccess: (created) => {
          onOpenChange(false);
          setName("");
          setCode("");
          setAddress("");
          setRegionId(NONE);
          void navigate({ to: "/sites/$siteId", params: { siteId: created.id } });
        },
      });
    } else {
      updateSite.mutate(body, { onSuccess: () => onOpenChange(false) });
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title={mode === "create" ? t("sites.new") : t("sites.editTitle")}
      footer={
        <>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            {t("common.cancel")}
          </Button>
          <Button onClick={submit} disabled={mutation.isPending || !name.trim()}>
            {mutation.isPending
              ? t("common.saving")
              : mode === "create"
                ? t("common.create")
                : t("common.save")}
          </Button>
        </>
      }
    >
      <form
        className="flex flex-col gap-4"
        onSubmit={(event) => {
          event.preventDefault();
          submit();
        }}
      >
        <FormError
          error={
            mutation.error instanceof ApiError && mutation.error.isFormLevel
              ? mutation.error
              : null
          }
        />

        <Field label={t("sites.name")} error={fieldErrors["name"]} required>
          {(props) => (
            <Input {...props} required value={name} onChange={(e) => setName(e.target.value)} />
          )}
        </Field>

        <Field label={t("sites.region")} error={fieldErrors["region_id"]}>
          {(props) => (
            <Select
              id={props.id}
              aria-invalid={props["aria-invalid"]}
              aria-describedby={props["aria-describedby"]}
              value={regionId}
              onValueChange={setRegionId}
              options={regionOptions}
            />
          )}
        </Field>

        <Field label={t("sites.code")} error={fieldErrors["code"]} hint={t("common.optional")}>
          {(props) => (
            <Input {...props} dir="ltr" value={code} onChange={(e) => setCode(e.target.value)} />
          )}
        </Field>

        <Field label={t("sites.address")} error={fieldErrors["address"]} hint={t("common.optional")}>
          {(props) => (
            <Input {...props} value={address} onChange={(e) => setAddress(e.target.value)} />
          )}
        </Field>
      </form>
    </Dialog>
  );
}

/**
 * Archive, not delete.
 *
 * The server refuses to delete a site that has ever been refilled — its fuel history is the
 * thing the customer bought. So the product does not have a delete: it has an archive, and
 * the dialog says exactly what survives, because a customer who has to guess assumes the
 * worst and never presses the button.
 */
function ArchiveSiteDialog({
  site,
  open,
  onOpenChange,
}: {
  site: Site;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { t } = useI18n();
  const updateSite = useUpdateSite(site.id);

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title={t("sites.archiveTitle", { name: site.name })}
      footer={
        <>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            {t("common.cancel")}
          </Button>
          {/* The button is named for what it does, and it is not red: nothing is destroyed. */}
          <Button
            variant="secondary"
            disabled={updateSite.isPending}
            onClick={() =>
              updateSite.mutate(
                { status: "inactive" },
                { onSuccess: () => onOpenChange(false) },
              )
            }
          >
            <Archive className="size-4" aria-hidden />
            {updateSite.isPending ? t("common.saving") : t("sites.archive")}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <FormError error={updateSite.error} />
        <p className="text-sm text-muted">{t("sites.archiveBody")}</p>
      </div>
    </Dialog>
  );
}

/* -------------------------------------------------------- tank dialogs */

function TankFormDialog({
  siteId,
  mode,
  tank,
  open,
  onOpenChange,
}: {
  siteId: string;
  mode: "create" | "edit";
  tank?: Tank;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { t, lang } = useI18n();
  const createTank = useCreateTank(siteId);
  const updateTank = useUpdateTank(siteId);
  const mutation = mode === "create" ? createTank : updateTank;

  const [name, setName] = useState(tank?.name ?? "");
  const [capacity, setCapacity] = useState(
    tank?.capacity_liters !== undefined ? String(tank.capacity_liters) : "",
  );
  const [fuelType, setFuelType] = useState<FuelType>(tank?.fuel_type ?? "diesel");
  const [threshold, setThreshold] = useState(
    tank?.low_level_threshold_percent !== undefined
      ? String(tank.low_level_threshold_percent)
      : "",
  );
  const [initial, setInitial] = useState("");

  const fieldErrors = useFieldErrors(mutation.error);

  const capacityNumber = Number(capacity);
  const initialNumber = initial === "" ? undefined : Number(initial);
  const currentLevel = tank?.current_level_liters;

  // The same two rules the server enforces, checked as the operator types — so the form
  // says what is wrong instead of handing them a 422 after they press Save.
  const overCapacity =
    initialNumber !== undefined &&
    Number.isFinite(capacityNumber) &&
    initialNumber > capacityNumber;

  const capacityBelowLevel =
    mode === "edit" &&
    currentLevel !== undefined &&
    Number.isFinite(capacityNumber) &&
    capacity !== "" &&
    capacityNumber < currentLevel;

  const submit = () => {
    if (mode === "create") {
      createTank.mutate(
        {
          name: name.trim(),
          capacity_liters: capacityNumber,
          fuel_type: fuelType,
          ...(threshold !== "" ? { low_level_threshold_percent: Number(threshold) } : {}),
          ...(initialNumber !== undefined ? { initial_level_liters: initialNumber } : {}),
        },
        {
          onSuccess: () => {
            onOpenChange(false);
            setName("");
            setCapacity("");
            setThreshold("");
            setInitial("");
            setFuelType("diesel");
          },
        },
      );
    } else if (tank) {
      updateTank.mutate(
        {
          tankId: tank.id,
          name: name.trim(),
          capacity_liters: capacityNumber,
          fuel_type: fuelType,
          ...(threshold !== "" ? { low_level_threshold_percent: Number(threshold) } : {}),
        },
        { onSuccess: () => onOpenChange(false) },
      );
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title={
        mode === "create" ? t("sites.addTank") : t("tank.editTitle", { name: tank?.name ?? "" })
      }
      footer={
        <>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            {t("common.cancel")}
          </Button>
          <Button
            onClick={submit}
            disabled={
              mutation.isPending ||
              !name.trim() ||
              !(capacityNumber > 0) ||
              overCapacity ||
              capacityBelowLevel
            }
          >
            {mutation.isPending
              ? t("common.saving")
              : mode === "create"
                ? t("common.create")
                : t("common.save")}
          </Button>
        </>
      }
    >
      <form
        className="flex flex-col gap-4"
        onSubmit={(event) => {
          event.preventDefault();
          submit();
        }}
      >
        <FormError
          error={
            mutation.error instanceof ApiError && mutation.error.isFormLevel
              ? mutation.error
              : null
          }
        />

        <Field label={t("tank.name")} error={fieldErrors["name"]} required>
          {(props) => (
            <Input {...props} required value={name} onChange={(e) => setName(e.target.value)} />
          )}
        </Field>

        <Field
          label={`${t("tank.capacity")} (${t("common.liters")})`}
          error={
            capacityBelowLevel && currentLevel !== undefined
              ? t("tank.capacityBelowLevel", { level: formatLiters(lang, currentLevel) })
              : fieldErrors["capacity_liters"]
          }
          required
        >
          {(props) => (
            <NumberInput
              {...props}
              required
              min={1}
              value={capacity}
              onChange={(e) => setCapacity(e.target.value)}
            />
          )}
        </Field>

        <Field label={t("tank.fuelType")} error={fieldErrors["fuel_type"]}>
          {(props) => (
            <Select
              id={props.id}
              aria-invalid={props["aria-invalid"]}
              aria-describedby={props["aria-describedby"]}
              value={fuelType}
              onValueChange={(value) => setFuelType(value as FuelType)}
              options={FUEL_TYPES.map((fuel) => ({
                value: fuel,
                label: t(`tank.fuel.${fuel}` as const),
              }))}
            />
          )}
        </Field>

        <Field
          label={`${t("tank.threshold")} (%)`}
          error={fieldErrors["low_level_threshold_percent"]}
          hint={t("tank.thresholdHint")}
        >
          {(props) => (
            <NumberInput
              {...props}
              min={1}
              max={100}
              value={threshold}
              onChange={(e) => setThreshold(e.target.value)}
            />
          )}
        </Field>

        {/* The opening level is a READING, not a refill — and a tank recorded low will
            raise a low-fuel alert the moment it is saved. That is correct, and the hint
            says so, so nobody is surprised by an alert they created themselves. */}
        {mode === "create" ? (
          <Field
            label={`${t("tank.initialLevel")} (${t("common.liters")})`}
            error={
              overCapacity
                ? t("refills.checkOverCapacity", { capacity })
                : fieldErrors["initial_level_liters"]
            }
            hint={t("tank.initialLevelHint")}
          >
            {(props) => (
              <NumberInput
                {...props}
                min={0}
                value={initial}
                onChange={(e) => setInitial(e.target.value)}
              />
            )}
          </Field>
        ) : null}
      </form>
    </Dialog>
  );
}

/**
 * Decommission a tank — never delete one.
 *
 * `deleteTank` exists in the spec and it 409s the moment a tank has a single recorded
 * refill: *"this tank has recorded refills — decommission it instead, so its fuel history
 * survives"*. Every tank a customer cares about has recorded refills. Offering Delete would
 * therefore be offering a button that fails, so the product does not have one.
 */
function DecommissionTankDialog({
  siteId,
  tank,
  open,
  onOpenChange,
}: {
  siteId: string;
  tank: Tank | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { t } = useI18n();
  const updateTank = useUpdateTank(siteId);

  if (!tank) return null;

  const retired = tank.status !== "active";

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title={
        retired
          ? t("tank.recommission")
          : t("tank.decommissionTitle", { name: tank.name })
      }
      footer={
        <>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            {t("common.cancel")}
          </Button>
          <Button
            variant="secondary"
            disabled={updateTank.isPending}
            onClick={() =>
              updateTank.mutate(
                { tankId: tank.id, status: retired ? "active" : "decommissioned" },
                { onSuccess: () => onOpenChange(false) },
              )
            }
          >
            {updateTank.isPending
              ? t("common.saving")
              : retired
                ? t("tank.recommission")
                : t("tank.decommission")}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <FormError error={updateTank.error} />
        {!retired ? <p className="text-sm text-muted">{t("tank.decommissionBody")}</p> : null}
      </div>
    </Dialog>
  );
}

/* --------------------------------------------------- generator dialogs */

function GeneratorFormDialog({
  siteId,
  mode,
  generator,
  open,
  onOpenChange,
}: {
  siteId: string;
  mode: "create" | "edit";
  generator?: Generator;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { t, lang } = useI18n();
  const createGenerator = useCreateGenerator(siteId);
  const updateGenerator = useUpdateGenerator(siteId);
  const mutation = mode === "create" ? createGenerator : updateGenerator;

  const [name, setName] = useState(generator?.name ?? "");
  const [kva, setKva] = useState(
    generator?.kva_rating !== undefined ? String(generator.kva_rating) : "",
  );
  const [hours, setHours] = useState(
    generator?.running_hours !== undefined ? String(generator.running_hours) : "",
  );
  const [manufacturer, setManufacturer] = useState(generator?.manufacturer ?? "");
  const [model, setModel] = useState(generator?.model ?? "");
  const [serial, setSerial] = useState(generator?.serial_number ?? "");
  const [status, setStatus] = useState(generator?.status ?? "active");

  const fieldErrors = useFieldErrors(mutation.error);

  const kvaNumber = Number(kva);
  const hoursNumber = hours === "" ? undefined : Number(hours);
  const currentHours = generator?.running_hours;

  /**
   * RUNNING HOURS ONLY EVER GO UP.
   *
   * The server 422s a decrease, and it is right to: an hour meter that appears to have gone
   * backwards is a transposed digit, not a generator running in reverse — and accepting it
   * would silently corrupt every consumption figure derived from it. So the form says so at
   * the field, before Save, rather than letting someone submit and collect a 422.
   */
  const hoursBackwards =
    mode === "edit" &&
    currentHours !== undefined &&
    hoursNumber !== undefined &&
    hoursNumber < currentHours;

  const submit = () => {
    if (mode === "create") {
      createGenerator.mutate(
        {
          name: name.trim(),
          kva_rating: kvaNumber,
          ...(manufacturer.trim() ? { manufacturer: manufacturer.trim() } : {}),
          ...(model.trim() ? { model: model.trim() } : {}),
          ...(serial.trim() ? { serial_number: serial.trim() } : {}),
          ...(hoursNumber !== undefined ? { running_hours: hoursNumber } : {}),
        },
        {
          onSuccess: () => {
            onOpenChange(false);
            setName("");
            setKva("");
            setHours("");
            setManufacturer("");
            setModel("");
            setSerial("");
          },
        },
      );
    } else if (generator) {
      updateGenerator.mutate(
        {
          generatorId: generator.id,
          name: name.trim(),
          kva_rating: kvaNumber,
          ...(manufacturer.trim() ? { manufacturer: manufacturer.trim() } : {}),
          ...(model.trim() ? { model: model.trim() } : {}),
          ...(serial.trim() ? { serial_number: serial.trim() } : {}),
          ...(hoursNumber !== undefined ? { running_hours: hoursNumber } : {}),
          status,
        },
        { onSuccess: () => onOpenChange(false) },
      );
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title={
        mode === "create"
          ? t("sites.addGenerator")
          : t("gen.editTitle", { name: generator?.name ?? "" })
      }
      footer={
        <>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            {t("common.cancel")}
          </Button>
          <Button
            onClick={submit}
            disabled={mutation.isPending || !name.trim() || !(kvaNumber > 0) || hoursBackwards}
          >
            {mutation.isPending
              ? t("common.saving")
              : mode === "create"
                ? t("common.create")
                : t("common.save")}
          </Button>
        </>
      }
    >
      <form
        className="flex flex-col gap-4"
        onSubmit={(event) => {
          event.preventDefault();
          submit();
        }}
      >
        <FormError
          error={
            mutation.error instanceof ApiError && mutation.error.isFormLevel
              ? mutation.error
              : null
          }
        />

        <Field label={t("gen.name")} error={fieldErrors["name"]} required>
          {(props) => (
            <Input {...props} required value={name} onChange={(e) => setName(e.target.value)} />
          )}
        </Field>

        <Field label={t("gen.kva")} error={fieldErrors["kva_rating"]} required>
          {(props) => (
            <NumberInput
              {...props}
              required
              min={1}
              value={kva}
              onChange={(e) => setKva(e.target.value)}
            />
          )}
        </Field>

        <Field
          label={t("gen.runningHours")}
          error={
            hoursBackwards && currentHours !== undefined
              ? t("refills.checkHoursBackwards", { current: formatHours(lang, currentHours) })
              : fieldErrors["running_hours"]
          }
          // The hint stands down once the error speaks: they say the same sentence, and a
          // field that tells you twice is a field that trusts you once.
          hint={
            hoursBackwards
              ? undefined
              : mode === "edit" && currentHours !== undefined
                ? t("gen.runningHoursHint", { current: formatHours(lang, currentHours) })
                : t("common.optional")
          }
        >
          {(props) => (
            <NumberInput
              {...props}
              // The floor is the meter's own last reading. The browser enforces it, the form
              // explains it, and the server is still the one that decides.
              min={mode === "edit" && currentHours !== undefined ? currentHours : 0}
              step="0.1"
              value={hours}
              onChange={(e) => setHours(e.target.value)}
            />
          )}
        </Field>

        <div className="grid gap-4 sm:grid-cols-2">
          <Field label={t("gen.manufacturer")} error={fieldErrors["manufacturer"]}>
            {(props) => (
              <Input
                {...props}
                value={manufacturer}
                onChange={(e) => setManufacturer(e.target.value)}
              />
            )}
          </Field>

          <Field label={t("gen.model")} error={fieldErrors["model"]}>
            {(props) => (
              <Input {...props} value={model} onChange={(e) => setModel(e.target.value)} />
            )}
          </Field>
        </div>

        <Field label={t("gen.serial")} error={fieldErrors["serial_number"]}>
          {(props) => (
            <Input
              {...props}
              dir="ltr"
              value={serial}
              onChange={(e) => setSerial(e.target.value)}
            />
          )}
        </Field>

        {/* Maintenance is a status, not a retirement: the generator is still at the site and
            still in the reports — it just is not available today. Decommissioning is the
            separate, deliberate act, and it has its own dialog that says what survives. */}
        {mode === "edit" ? (
          <Field label={t("gen.statusLabel")} error={fieldErrors["status"]}>
            {(props) => (
              <Select
                id={props.id}
                aria-invalid={props["aria-invalid"]}
                aria-describedby={props["aria-describedby"]}
                value={status}
                onValueChange={(value) => setStatus(value as Generator["status"])}
                options={[
                  { value: "active", label: t("gen.status.active") },
                  { value: "maintenance", label: t("gen.status.maintenance") },
                  {
                    value: "decommissioned",
                    label: t("gen.status.decommissioned"),
                    disabled: true,
                    hint: t("gen.decommission"),
                  },
                ]}
              />
            )}
          </Field>
        ) : null}
      </form>
    </Dialog>
  );
}

function DecommissionGeneratorDialog({
  siteId,
  generator,
  open,
  onOpenChange,
}: {
  siteId: string;
  generator: Generator | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { t } = useI18n();
  const updateGenerator = useUpdateGenerator(siteId);

  if (!generator) return null;

  const retired = generator.status === "decommissioned";

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title={
        retired
          ? t("gen.recommission")
          : t("gen.decommissionTitle", { name: generator.name })
      }
      footer={
        <>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            {t("common.cancel")}
          </Button>
          <Button
            variant="secondary"
            disabled={updateGenerator.isPending}
            onClick={() =>
              updateGenerator.mutate(
                {
                  generatorId: generator.id,
                  status: retired ? "active" : "decommissioned",
                },
                { onSuccess: () => onOpenChange(false) },
              )
            }
          >
            {updateGenerator.isPending
              ? t("common.saving")
              : retired
                ? t("gen.recommission")
                : t("gen.decommission")}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <FormError error={updateGenerator.error} />
        {!retired ? <p className="text-sm text-muted">{t("gen.decommissionBody")}</p> : null}
      </div>
    </Dialog>
  );
}
